export const RELOAD_WINDOW_MS = 60_000
export const RELOAD_MARK_KEY = 'reconforta-preload-error-reloaded-at'

interface Environment {
  storage(): Pick<Storage, 'getItem' | 'setItem'>
  reload(): void
  now(): number
  online(): boolean
}

export interface PreloadErrorRecovery {
  recover(failure: unknown): boolean
  reloading(error: unknown): boolean
}

// Never preventDefault: a prevented event makes the import RESOLVE with undefined inside its caller.
export function createPreloadErrorRecovery(env: Environment): PreloadErrorRecovery {
  const answered = new WeakSet<object>()
  let reloading = false
  const remember = (failure: unknown) => {
    if (typeof failure === 'object' && failure !== null) answered.add(failure)
  }

  return {
    recover(failure) {
      if (reloading) {
        remember(failure)
        return true
      }
      if (!env.online()) return false
      const at = env.now()
      try {
        const store = env.storage()
        const elapsed = at - Number(store.getItem(RELOAD_MARK_KEY))
        if (elapsed >= 0 && elapsed < RELOAD_WINDOW_MS) return false
        store.setItem(RELOAD_MARK_KEY, String(at))
      } catch {
        return false
      }
      remember(failure)
      reloading = true
      env.reload()
      return true
    },
    reloading: (error) => typeof error === 'object' && error !== null && answered.has(error),
  }
}

export const preloadErrorRecovery = createPreloadErrorRecovery({
  storage: () => window.sessionStorage,
  reload: () => window.location.reload(),
  now: Date.now,
  online: () => navigator.onLine,
})

export function installPreloadErrorRecovery(): void {
  window.addEventListener('vite:preloadError', (event) => {
    preloadErrorRecovery.recover(event.payload)
  })
}
